import Message from "sap/ui/core/message/Message";
import MessageType from "sap/ui/core/message/MessageType";
import { ValueState } from "sap/ui/core/library";
import Control from "sap/ui/core/Control";
import FormContainer from "sap/ui/layout/form/FormContainer";
import FormElement from "sap/ui/layout/form/FormElement";
import IconTabFilter from "sap/m/IconTabFilter";

class Validator {
    private _isValid: boolean;
    private _isValidationPerformed: boolean;
    private _aPossibleAggregations: string[];
    private _aValidateProperties: string[];

    constructor() {
        this._isValid = true;
        this._isValidationPerformed = false;
        this._aPossibleAggregations = [
            "items",
            "content",
            "form",
            "formContainers",
            "formElements",
            "fields",
            "sections",
            "subSections",
            "_grid",
            "cells",
            "_page"
        ];
        this._aValidateProperties = ["value", "selectedKey", "text"];
    }

    /**
     * Returns true _only_ when the form validation has been performed, and no validation errors were found
     * @returns {boolean}
     */
    isValid(): boolean {
        return this._isValidationPerformed && this._isValid;
    }

    /**
     * Recursively validates the given oControl and any aggregations (i.e. child controls) it may have
     * @param {(Control|FormContainer|FormElement)} oControl - The control or element to be validated.
     * @return {boolean} whether the oControl is valid or not.
     */
    validate(oControl: Control | FormContainer | FormElement | any): boolean {
        this._isValid = true;
        this._validate(oControl);
        return this.isValid();
    }

    /**
     * Clear the value state of all the controls
     * @param {(Control|FormContainer|FormElement)} oControl - The control or element to be validated.
     */
    clearValueState(oControl: Control | FormContainer | FormElement): void {
        if (!oControl) return;

        if ((oControl as any).setValueState) (oControl as any).setValueState(ValueState.None);

        this._recursiveCall(oControl, this.clearValueState.bind(this));
    }

    /**
     * Recursively validates the given oControl and any aggregations (i.e. child controls) it may have
     * @param {(Control|FormContainer|FormElement)} oControl - The control or element to be validated.
     */
    private _validate(oControl: Control | FormContainer | FormElement): void {
        let isValidatedControl = true;
        let isValid = true;

        if (
            !(
                (oControl instanceof Control ||
                    oControl instanceof FormContainer ||
                    oControl instanceof FormElement ||
                    oControl instanceof IconTabFilter) &&
                oControl.getVisible()
            )
        ) {
            return;
        }

        if (
            (oControl as any).getRequired &&
            (oControl as any).getRequired() === true &&
            (oControl as any).getEnabled &&
            (oControl as any).getEnabled() === true
        ) {
            isValid = this._validateRequired(oControl);
        } else if (
            this._hasType(oControl) !== -1 &&
            (oControl as any).getEnabled &&
            (oControl as any).getEnabled() === true
        ) {
            isValid = this._validateConstraint(oControl, this._hasType(oControl));
        } else if (
            (oControl as any).getValueState &&
            (oControl as any).getValueState() === ValueState.Error
        ) {
            isValid = false;
            this._setValueState(oControl, ValueState.Error, "Wrong input");
        } else {
            isValidatedControl = false;
        }

        if (!isValid) {
            this._isValid = false;
            this._addMessage(oControl);
        }

        if (!isValidatedControl) {
            this._recursiveCall(oControl, this._validate.bind(this));
        }
        this._isValidationPerformed = true;
    }

    /**
     * Check if the control is required
     * @param {(Control|FormContainer|FormElement)} oControl - The control or element to be validated.
     * @return {boolean} this._isValid - If the property is valid
     */
    private _validateRequired(oControl: Control | FormContainer | FormElement): boolean {
        let isValid = true;

        for (let i = 0; i < this._aValidateProperties.length; i += 1) {
            try {
                (oControl as any).getBinding(this._aValidateProperties[i]);
                const oExternalValue = (oControl as any).getProperty(this._aValidateProperties[i]);

                if (!oExternalValue || oExternalValue === "") {
                    this._setValueState(oControl, ValueState.Error, "Please fill this mandatory field!");
                    isValid = false;
                } else if (
                    (oControl as any).getAggregation("picker") &&
                    (oControl as any).getProperty("selectedKey").length === 0
                ) {
                    this._setValueState(oControl, ValueState.Error, "Please choose an entry!");
                    isValid = false;
                } else {
                    (oControl as any).setValueState(ValueState.None);
                    isValid = true;
                    break;
                }
            } catch (ex) {
                // Validation failed
            }
        }
        return isValid;
    }

    /**
     * Check if the control is required
     * @param {(Control|FormContainer|FormElement)} oControl - The control or element to be validated.
     * @param {number} i - The index of the property
     * @return {boolean} this._isValid - If the property is valid
     */
    private _validateConstraint(oControl: Control | FormContainer | FormElement, i: number): boolean {
        let isValid = true;
        let editable: boolean;

        try {
            editable = (oControl as any).getProperty("editable");
        } catch (ex) {
            editable = true;
        }

        if (editable) {
            try {
                const oControlBinding = (oControl as any).getBinding(this._aValidateProperties[i]);
                const oExternalValue = (oControl as any).getProperty(this._aValidateProperties[i]);
                const oInternalValue = oControlBinding.getType().parseValue(oExternalValue, oControlBinding.sInternalType);
                oControlBinding.getType().validateValue(oInternalValue);
                (oControl as any).setValueState(ValueState.None);
            } catch (ex) {
                isValid = false;
                this._setValueState(oControl, ValueState.Error, ex.message);
            }
        }
        return isValid;
    }

    /**
     * Add message to the MessageManager
     * @param {(Control|FormContainer|FormElement)} oControl - The control or element to be validated.
     * @param {string} sMessage - Customize the message
     */
    private _addMessage(oControl: Control | FormContainer | FormElement, sMessage?: string): void {
        let sLabel: string | undefined;
        let eMessageType = MessageType.Error;

        if (sMessage === undefined) sMessage = "Wrong input";

        switch ((oControl as any).getMetadata().getName()) {
            case "sap.m.CheckBox":
            case "sap.m.Input":
            case "sap.m.Select":
                sLabel = (oControl.getParent() as any).getLabel().getText();
                break;
        }

        if ((oControl as any).getValueState)
            eMessageType = this._convertValueStateToMessageType((oControl as any).getValueState());

        if ((oControl as any).getValueStateText().startsWith("Geben Sie ein Datum")) {
            (oControl as any).setValueStateText("Geben Sie ein Datum im Format DD.MM.YYYY ein.");
            sLabel = "Einstalldatum";
        }

        sap.ui.getCore().getMessageManager().addMessages(
            new Message({
                message: (oControl as any).getValueStateText ? (oControl as any).getValueStateText() : sMessage,
                type: eMessageType,
                additionalText: sLabel
            })
        );
    }

    /**
     * Check if the control property has a data type, then returns the index of the property to validate
     * @param {(Control|FormContainer|FormElement)} oControl - The control or element to be validated.
     * @return {number} i - The index of the property to validate
     */
    private _hasType(oControl: Control | FormContainer | FormElement): number {
        for (let i = 0; i < this._aValidateProperties.length; i += 1) {
            if (
                (oControl as any).getBinding(this._aValidateProperties[i]) &&
                (oControl as any).getBinding(this._aValidateProperties[i]).getType()
            )
                return i;
        }
        return -1;
    }

    /**
     * Set ValueState and ValueStateText of the control
     * @param {Control|FormContainer|FormElement} oControl - The control or element to be validated.
     * @param {ValueState} eValueState - The ValueState to be set
     * @param {string} sText - The ValueStateText to be set
     */
    private _setValueState(oControl: Control | FormContainer | FormElement, eValueState: ValueState, sText: string): void {
        (oControl as any).setValueState(eValueState);
        if ((oControl as any).getValueStateText && !(oControl as any).getValueStateText())
            (oControl as any).setValueStateText(sText);
    }

    /**
     * Recursively calls the function on all the children of the aggregation
     * @param {(Control|FormContainer|FormElement)} oControl - The control or element to be validated.
     * @param {function} fFunction - The function to call recursively
     */
    private _recursiveCall(oControl: Control | FormContainer | FormElement, fFunction: (oControl: Control | FormContainer | FormElement) => void): void {
        for (let i = 0; i < this._aPossibleAggregations.length; i += 1) {
            const aControlAggregation = (oControl as any).getAggregation(this._aPossibleAggregations[i]);

            if (!aControlAggregation) continue;

            if (Array.isArray(aControlAggregation)) {
                for (let j = 0; j < aControlAggregation.length; j += 1) {
                    fFunction.call(this, aControlAggregation[j]);
                }
            } else {
                fFunction.call(this, aControlAggregation);
            }
        }
    }

    /**
     * Recursively calls the function on all the children of the aggregation
     * @param {ValueState} eValueState
     * @return {MessageType} eMessageType
     */
    private _convertValueStateToMessageType(eValueState: ValueState): MessageType {
        let eMessageType: MessageType;

        switch (eValueState) {
            case ValueState.Error:
                eMessageType = MessageType.Error;
                break;
            case ValueState.Information:
                eMessageType = MessageType.Information;
                break;
            case ValueState.None:
                eMessageType = MessageType.None;
                break;
            case ValueState.Success:
                eMessageType = MessageType.Success;
                break;
            case ValueState.Warning:
                eMessageType = MessageType.Warning;
                break;
            default:
                eMessageType = MessageType.Error;
        }
        return eMessageType;
    }
}

export default Validator;